import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'

import { isDemoPage } from '../../../../../utils/hooks'
import COLORS from '../../../../../styles/colors.scss'
import {
  DEMO_INVOICE_READY_EVENT,
  DEMO_INVOICE_SAVE_EVENT,
  DEMO_PACKAGING_COMPLETE_EVENT,
  DEMO_PRODUCT_SAVE_EVENT,
} from '../../playback-constants'

type UseDemoPackagingActionParams = {
  action: string
  disabled?: boolean
  isAllowed?: boolean
}

export const useDemoPackagingAction = ({
  action,
  disabled = false,
  isAllowed = false,
}: UseDemoPackagingActionParams) => {
  const isDemoMode = isDemoPage()

  return {
    buttonProps: {
      'data-demo-allowed': isDemoMode && isAllowed ? action : undefined,
      'data-demo-playback-action': isDemoMode ? action : undefined,
    },
    disabled: isDemoMode ? false : disabled,
    isDemoMode,
  }
}

export const useDemoPackagingPlaybackComplete = () => {
  const [ isComplete, setIsComplete ] = useState(false)

  useEffect(() => {
    const handleComplete = () => setIsComplete(true)

    window.addEventListener(DEMO_PACKAGING_COMPLETE_EVENT, handleComplete)

    return () => window.removeEventListener(DEMO_PACKAGING_COMPLETE_EVENT, handleComplete)
  }, [])

  return isComplete
}

export const useDemoInvoiceForm = (isSaveButtonActive: boolean) => {
  const isDemoMode = isDemoPage()
  const [ isPlaybackReady, setIsPlaybackReady ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleReady = () => setIsPlaybackReady(true)

    window.addEventListener(DEMO_INVOICE_READY_EVENT, handleReady)

    return () => window.removeEventListener(DEMO_INVOICE_READY_EVENT, handleReady)
  }, [ isDemoMode ])

  return {
    saveButtonDisabled: isDemoMode ? !isPlaybackReady : !isSaveButtonActive,
    saveButtonProps: {
      'data-demo-playback-action': isDemoMode ? 'save-invoice' : undefined,
    },
  }
}

export const useDemoInvoiceDetails = (invoiceId?: number) => {
  const isDemoMode = isDemoPage()
  const [ isSaved, setIsSaved ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleSave = () => setIsSaved(true)

    window.addEventListener(DEMO_INVOICE_SAVE_EVENT, handleSave)

    return () => window.removeEventListener(DEMO_INVOICE_SAVE_EVENT, handleSave)
  }, [ isDemoMode ])

  return {
    isDemoMode,
    shouldShowCompleted: Boolean(invoiceId) && (!isDemoMode || isSaved),
    shouldShowProducts: !isDemoMode || isSaved,
  }
}

export const useDemoProductsSummary = () => {
  const isDemoMode = isDemoPage()
  const [ isProductSaved, setIsProductSaved ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleSave = () => setIsProductSaved(true)

    window.addEventListener(DEMO_PRODUCT_SAVE_EVENT, handleSave)

    return () => window.removeEventListener(DEMO_PRODUCT_SAVE_EVENT, handleSave)
  }, [ isDemoMode ])

  return !isDemoMode || isProductSaved
}

export const useDemoExpandedInvoice = (openByDefault?: boolean) => {
  const isDemoMode = isDemoPage()

  if (!isDemoMode) {
    return {
      defaultExpanded: openByDefault,
    }
  }

  return {
    expanded: true,
    onChange: () => undefined,
  }
}

export const useDemoInvoiceTitle = (invoiceId?: number) => {
  const isDemoMode = isDemoPage()
  const [ isInvoiceSaved, setIsInvoiceSaved ] = useState(false)
  const [ isProductSaved, setIsProductSaved ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleInvoiceSaved = () => setIsInvoiceSaved(true)
    const handleProductSaved = () => setIsProductSaved(true)

    window.addEventListener(DEMO_INVOICE_SAVE_EVENT, handleInvoiceSaved)
    window.addEventListener(DEMO_PRODUCT_SAVE_EVENT, handleProductSaved)

    return () => {
      window.removeEventListener(DEMO_INVOICE_SAVE_EVENT, handleInvoiceSaved)
      window.removeEventListener(DEMO_PRODUCT_SAVE_EVENT, handleProductSaved)
    }
  }, [ isDemoMode ])

  return {
    shouldShowProductInformation: !isDemoMode || isProductSaved,
    visibleInvoiceId: !isDemoMode || isInvoiceSaved ? invoiceId : undefined,
  }
}

type UseDemoDeliveryTypeParams = {
  combinedCargo: number
  containers: number
  deliveryMethod?: number
  demoDeliveryMethod?: number
  getCombinedCargoText(): string
  getContainersText(): string
}

export const useDemoDeliveryType = ({
  combinedCargo,
  containers,
  deliveryMethod,
  demoDeliveryMethod,
  getCombinedCargoText,
  getContainersText,
}: UseDemoDeliveryTypeParams) => {
  const isDemoMode = isDemoPage()

  const getDeliveryMethodText = (selected: unknown) => {
    if (selected === containers) {
      return getContainersText()
    }

    if (selected === combinedCargo) {
      return getCombinedCargoText()
    }

    return ''
  }

  const renderValue = (selected: unknown) => (
    <Typography component='span' variant='body1' sx={{ color: COLORS.BLACK }}>
      {getDeliveryMethodText(selected)}
    </Typography>
  )

  return {
    combinedCargoProps: {
      'data-demo-allowed': isDemoMode ? 'delivery-method-option' : undefined,
      'data-demo-delivery-method': isDemoMode ? combinedCargo : undefined,
    },
    containersProps: {
      'data-demo-allowed': isDemoMode ? 'delivery-method-option' : undefined,
      'data-demo-delivery-method': isDemoMode ? containers : undefined,
    },
    selectProps: {
      'data-demo-allowed': isDemoMode ? 'delivery-method-select' : undefined,
      'data-demo-delivery-method-target': isDemoMode ? demoDeliveryMethod : undefined,
      'data-demo-playback-action': isDemoMode ? 'delivery-method-select' : undefined,
      renderValue: isDemoMode ? renderValue : undefined,
      SelectDisplayProps: isDemoMode ? {
        style: {
          color: COLORS.BLACK,
          opacity: 1,
          WebkitTextFillColor: COLORS.BLACK,
        },
      } : undefined,
      value: isDemoMode && deliveryMethod === undefined ? '' : deliveryMethod,
    },
  }
}

export const useDemoProductsTable = (openProductsTable: () => void) => {
  const isDemoMode = isDemoPage()

  return {
    buttonProps: {
      'data-demo-allowed': isDemoMode ? 'products-table' : undefined,
      onClick: isDemoMode ? undefined : openProductsTable,
    },
    tooltipProps: {
      disableFocusListener: !isDemoMode,
      disableHoverListener: !isDemoMode,
    },
    shouldShowTooltip: isDemoMode,
  }
}

type UseDemoPackagingSummaryParams = {
  currentDeliveryType?: number
  deliveryType?: number
  hasSummary: boolean
  setDeliveryMethod(deliveryMethod?: number): void
}

export const useDemoPackagingSummary = ({
  currentDeliveryType,
  deliveryType,
  hasSummary,
  setDeliveryMethod,
}: UseDemoPackagingSummaryParams) => {
  const isDemoMode = isDemoPage()
  const [ isDeliveryConfirmed, setIsDeliveryConfirmed ] = useState(false)

  useEffect(() => {
    if (!hasSummary) {
      return
    }

    setDeliveryMethod(isDemoMode ? undefined : currentDeliveryType)
  }, [ currentDeliveryType, hasSummary, isDemoMode, setDeliveryMethod ])

  let savedDeliveryType = deliveryType || currentDeliveryType

  if (isDemoMode && !isDeliveryConfirmed) {
    savedDeliveryType = undefined
  }

  return {
    confirmDelivery: () => setIsDeliveryConfirmed(true),
    demoDeliveryMethod: isDemoMode ? currentDeliveryType : undefined,
    savedDeliveryType,
  }
}
